import { useEffect } from 'react';

const SITE_URL = 'https://www.onwardland.com';
const LOGO_URL = 'https://www.onwardland.com/brand/onward-approved-logo.png';

/**
 * Injects a JSON-LD script tag into <head>. Removes it on unmount to prevent duplication.
 */
export const useJsonLd = (id, data) => {
  useEffect(() => {
    if (!data) return;

    const existing = document.getElementById(id);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [id, data]);
};

/**
 * Organization schema for Onward Land Solutions.
 *
 * Deliberately uses @type: Organization (not ProfessionalService or LocalBusiness) because
 * Onward Land Solutions does NOT provide licensed surveying services. We list land-side
 * services that do not require professional survey licensure.
 */
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  "name": "Onward Land Solutions",
  "alternateName": "Onward",
  "url": SITE_URL,
  "logo": LOGO_URL,
  "telephone": "+1-817-577-5024",
  "email": "info@onwardland.com",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Fort Worth",
    "addressRegion": "TX",
    "addressCountry": "US"
  },
  "areaServed": [
    { "@type": "State", "name": "Texas" },
    { "@type": "Country", "name": "United States" }
  ],
  "description": "Onward Land Solutions is a land project management, right-of-way, corridor intelligence, easement acquisition, title/ownership, GIS-backed project tracking, and survey coordination company serving infrastructure, energy, utility, pipeline, transmission, renewable, and development clients.",
  "knowsAbout": [
    "Land Project Management",
    "Right-of-Way Acquisition",
    "Easement Acquisition",
    "Corridor Intelligence",
    "Route Risk Review",
    "GIS Parcel Tracking",
    "Title and Ownership Research",
    "Survey Coordination",
    "Field Access Management",
    "Renewable Energy Land Services",
    "Pipeline Right-of-Way",
    "Transmission Right-of-Way",
    "Infrastructure Land Services",
    "Project Reporting and Controls"
  ],
  "sameAs": [
    "https://www.linkedin.com/company/onward-land-solutions"
  ]
};

// Kept as alias so existing imports do not break during the transition. Points to the same schema.
export const localBusinessSchema = organizationSchema;

/**
 * Build FAQPage schema from array of {question, answer}
 */
export const buildFaqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

/**
 * Build Article schema for insights posts
 */
export const buildArticleSchema = ({ title, slug, author, publishDate, modifiedDate, image, excerpt }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": title,
  "author": {
    "@type": "Organization",
    "name": author || "Onward Land Solutions",
    "url": SITE_URL
  },
  "publisher": {
    "@type": "Organization",
    "name": "Onward Land Solutions",
    "url": SITE_URL,
    "logo": {
      "@type": "ImageObject",
      "url": LOGO_URL
    }
  },
  "datePublished": publishDate,
  "dateModified": modifiedDate || publishDate,
  "image": image,
  "description": excerpt,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `${SITE_URL}/insights/${slug}`
  }
});

/**
 * Build BreadcrumbList schema
 */
export const buildBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url ? `${SITE_URL}${item.url}` : undefined
  }))
});
